import { useEffect, useRef, useState, useCallback } from "react"
import { useCookieConsent } from "@/context/cookie-consent-context"
import { useTranslation } from "@/hooks/use-translation"
import { ADS_CONFIG } from "@/config/ads"

declare global {
  interface Window {
    adsbygoogle?: any[]
  }
}

interface AdSenseProps {
  adSlot: string
  adFormat?: string
  adLayout?: string
  adLayoutKey?: string
  className?: string
  style?: React.CSSProperties
  compact?: boolean
}

// Global tracking for AdSense script and initialization
const globalAdSenseLoaded = { value: false };
const globalAdSenseInitialized = new Set<string>();

function AdSenseComponent({
  adSlot,
  adFormat = "auto",
  adLayout,
  adLayoutKey,
  className = "",
  style = {},
  compact = false
}: AdSenseProps) {
  const { preferences, hasConsented } = useCookieConsent()
  const { t } = useTranslation()
  const adRef = useRef<HTMLDivElement>(null)
  const [adLoaded, setAdLoaded] = useState(false)
  const [adError, setAdError] = useState(false)
  const mountedRef = useRef(false)
  const adKeyRef = useRef(`adsense-${adSlot}-${Date.now()}`)

  // Only show and initialize ads if user has consented to advertising cookies
  const shouldShowAds = hasConsented && preferences.advertising
  const shouldInitialize = ADS_CONFIG.ENABLE_ADS && shouldShowAds

  // Safe cleanup function that handles all edge cases
  const safeCleanup = useCallback((container: HTMLElement) => {
    if (!container) return

    try {
      // Remove all child nodes iteratively
      while (container.firstChild) {
        try {
          container.removeChild(container.firstChild)
        } catch (err) {
          // If removeChild fails, try a different approach
          try {
            if (container.firstChild && container.firstChild.parentNode === container) {
              container.firstChild.remove()
            } else {
              break // Exit if we can't remove safely
            }
          } catch (innerErr) {
            break // Exit if remove() also fails
          }
        }
      }

      // Fallback: clear innerHTML if children still exist
      if (container.children.length > 0) {
        container.innerHTML = ''
      }

      // Remove any data attributes
      container.removeAttribute('data-ad-status')
      container.removeAttribute('data-adsbygoogle-status')
      container.style.display = 'block'


    } catch (error) {
      console.warn('🧹 AdSense cleanup had issues:', error)
      // Last resort
      try {
        container.innerHTML = ''
      } catch (finalError) {
        console.error('🧹 Complete AdSense cleanup failure:', finalError)
      }
    }
  }, [])

  // Initialize individual ad - Only when consent is granted
  const initializeAd = useCallback(() => {
    if (!adRef.current || !shouldInitialize || !globalAdSenseLoaded.value) return

    const container = adRef.current
    const adKey = adKeyRef.current

    // Prevent double initialization
    if (globalAdSenseInitialized.has(adKey)) {
      return
    }

    // Check if container has proper dimensions
    const containerRect = container.getBoundingClientRect()
    if (containerRect.width < 300) {
      return
    }

    try {


      // Clean container first
      safeCleanup(container)

      // Create ad element
      const adElement = document.createElement('ins')
      adElement.className = 'adsbygoogle'
      adElement.style.display = 'block'
      adElement.setAttribute('data-ad-client', ADS_CONFIG.ADSENSE_PUBLISHER_ID)
      adElement.setAttribute('data-ad-slot', adSlot)
      adElement.setAttribute('data-ad-format', adFormat)

      if (adLayout) {
        adElement.setAttribute('data-ad-layout', adLayout)
      }
      if (adLayoutKey) {
        adElement.setAttribute('data-ad-layout-key', adLayoutKey)
      }

      // Set proper dimensions for responsive ads
      if (adFormat === 'auto' || adFormat === 'fluid') {
        adElement.style.width = '100%'
        adElement.style.height = 'auto'
        adElement.style.minHeight = '250px'
        adElement.setAttribute('data-full-width-responsive', 'true')
      }

      container.appendChild(adElement)

      // Initialize with adsbygoogle with proper error handling
      setTimeout(() => {
        try {
          if (window.adsbygoogle && window.adsbygoogle.push) {
            window.adsbygoogle.push({})
            globalAdSenseInitialized.add(adKey)
            setAdLoaded(true)
            setAdError(false)

          } else {
            throw new Error('adsbygoogle not available')
          }
        } catch (error) {
          console.error('📺 Error initializing ad:', error)
          setAdError(true)
          setAdLoaded(false)
          // Remove from initialized set on error
          globalAdSenseInitialized.delete(adKey)
        }
      }, 100)

    } catch (error) {
      console.error('📺 Error setting up ad:', error)
      setAdError(true)
      setAdLoaded(false)
    }
  }, [adSlot, adFormat, adLayout, adLayoutKey, shouldInitialize, safeCleanup])

  // Load AdSense script - Only if ads are enabled and consent is granted
  useEffect(() => {
    if (!shouldInitialize) return

    // Prevent double execution in StrictMode
    if (mountedRef.current) return
    mountedRef.current = true

    const loadAdSenseScript = () => {
      const scriptId = 'adsense-script'
      const existingScript = document.getElementById(scriptId)

      if (existingScript || globalAdSenseLoaded.value) {
        globalAdSenseLoaded.value = true
        // Initialize if consent is granted
        setTimeout(() => {
          if (mountedRef.current) initializeAd()
        }, 200)
        return
      }



      const script = document.createElement('script')
      script.id = scriptId
      script.async = true
      script.crossOrigin = 'anonymous'
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADS_CONFIG.ADSENSE_PUBLISHER_ID}`

      script.onload = () => {

        globalAdSenseLoaded.value = true

        // Initialize adsbygoogle array
        if (!window.adsbygoogle) {
          window.adsbygoogle = []
        }

        // Initialize ad
        setTimeout(() => {
          if (mountedRef.current) initializeAd()
        }, 200)
      }

      script.onerror = () => {
        console.error('📺 Failed to load AdSense script')
        setAdError(true)
      }

      try {
        document.head.appendChild(script)
      } catch (error) {
        console.error('📺 Error appending AdSense script:', error)
        setAdError(true)
      }
    }

    loadAdSenseScript()

    return () => {
      // Don't remove script in cleanup to avoid StrictMode issues
      // Reset initialization flag for this specific ad
      const adKey = adKeyRef.current
      globalAdSenseInitialized.delete(adKey)
    }
  }, [shouldInitialize, initializeAd])

  // Clean up ads when consent is withdrawn
  useEffect(() => {
    if (hasConsented && !preferences.advertising && adRef.current) {

      safeCleanup(adRef.current)
      setAdLoaded(false)
      setAdError(false)
      const adKey = adKeyRef.current
      globalAdSenseInitialized.delete(adKey)
    }
  }, [preferences.advertising, hasConsented, safeCleanup])

  // Show consent message when no advertising consent
  if (!ADS_CONFIG.ENABLE_ADS) {
    // En desarrollo, mostrar placeholder para ver el layout
    if (import.meta.env.DEV) {
      return (
        <div className={`adsense-container ${className}`} style={{ ...style, minWidth: compact ? '0' : '300px', width: '100%' }}>
          <div className={`ad-development-placeholder bg-primary/5 dark:bg-primary/10 border-2 border-dashed border-primary/30 dark:border-primary/40 rounded-lg text-center transition-colors ${compact ? 'p-3' : 'p-8'}`}>
            <div className={`flex items-center justify-center ${compact ? 'gap-2' : 'flex-col space-y-2'}`}>
              <div className={`bg-primary/20 dark:bg-primary/30 rounded-lg flex items-center justify-center transition-colors ${compact ? 'w-6 h-6' : 'w-12 h-12'}`}>
                <span className={`text-primary ${compact ? 'text-sm' : 'text-xl'}`}>🚧</span>
              </div>
              <div className={compact ? 'text-left min-w-0' : ''}>
                <p className={`text-primary font-medium ${compact ? 'text-xs leading-tight' : 'text-sm'}`}>
                  {compact ? 'Ad Placeholder' : 'AdSense Placeholder (Dev)'}
                </p>
                {!compact && (
                  <p className="text-primary/70 dark:text-primary/60 text-xs">
                    Ad slot: {adSlot} • Enable ads in production
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  if (!shouldShowAds) {
    return (
      <div className={`adsense-container ${className}`} style={{ ...style, minWidth: compact ? '0' : '300px', width: '100%' }}>
        <div className={`ad-consent-message bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-lg text-center transition-colors ${compact ? 'p-3' : 'p-8'}`}>
          <div className={`flex items-center justify-center ${compact ? 'gap-2' : 'flex-col space-y-2'}`}>
            <div className={`bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center transition-colors ${compact ? 'w-6 h-6' : 'w-12 h-12'}`}>
              <span className={`text-gray-400 dark:text-gray-500 ${compact ? 'text-sm' : 'text-xl'}`}>🍪</span>
            </div>
            <div className={compact ? 'text-left min-w-0' : ''}>
              <p className={`text-gray-500 dark:text-gray-400 font-medium ${compact ? 'text-xs leading-tight' : 'text-sm'}`}>
                {compact ? t("ads.consentRequired").substring(0, 20) + "..." : t("ads.consentRequired")}
              </p>
              {!compact && (
                <p className="text-gray-400 dark:text-gray-500 text-xs">
                  {t("ads.consentRequiredDesc")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`adsense-container ${className}`}
      style={{
        ...style,
        minWidth: compact ? '0' : '300px',
        width: '100%'
      }}
    >
      {/* Placeholder for when ads aren't loaded yet */}
      {!adLoaded && !adError && (
        <div className={`ad-placeholder bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-lg text-center transition-colors ${compact ? 'p-3' : 'p-8'}`}>
          <div className={`flex items-center justify-center ${compact ? 'gap-2' : 'flex-col space-y-2'}`}>
            <div className={`bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center transition-colors ${compact ? 'w-6 h-6' : 'w-12 h-12'}`}>
              <span className={`text-gray-400 dark:text-gray-500 ${compact ? 'text-sm' : 'text-xl'}`}>📺</span>
            </div>
            <div className={compact ? 'text-left min-w-0' : ''}>
              <div className="animate-pulse">
                <p className={`text-gray-500 dark:text-gray-400 font-medium ${compact ? 'text-xs leading-tight' : 'text-sm'}`}>
                  {compact ? t("ads.loading") : t("ads.placeholder")}
                </p>
              </div>
              {!compact && (
                <p className="text-gray-400 dark:text-gray-500 text-xs">
                  {t("ads.placeholderDesc")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {adError && (
        <div className={`ad-error text-center text-sm text-muted-foreground border border-gray-200 dark:border-gray-600 rounded bg-red-50 dark:bg-red-900/20 transition-colors ${compact ? 'p-2' : 'p-4'}`}>
          <p className={compact ? 'text-xs' : ''}>{compact ? "Error" : t("ads.error")}</p>
        </div>
      )}

      <div
        ref={adRef}
        className="adsense-ad-container"
        style={{
          minHeight: compact ? '100px' : '250px',
          minWidth: compact ? '0' : '300px',
          width: '100%',
          display: adLoaded ? 'block' : 'none',
          textAlign: 'center',
          position: 'relative'
        }}
      />

      {adLoaded && !adError && (
        <div className="ad-disclaimer text-xs text-muted-foreground dark:text-gray-500 text-center mt-1 transition-colors">
          {t("ads.disclaimer")}
        </div>
      )}
    </div>
  )
}

// Pre-configured ad components
export const ResponsiveAd = ({ className, ...props }: Omit<AdSenseProps, 'adSlot'>) => (
  <AdSenseComponent
    adSlot={ADS_CONFIG.AD_SLOTS.RESPONSIVE}
    adFormat="auto"
    className={className}
    {...props}
  />
)

export const InContentAd = ({ className, ...props }: Omit<AdSenseProps, 'adSlot'>) => (
  <AdSenseComponent
    adSlot={ADS_CONFIG.AD_SLOTS.IN_CONTENT}
    adFormat="fluid"
    adLayout="in-article"
    className={className}
    {...props}
  />
)

export const HeaderAd = ({ className, ...props }: Omit<AdSenseProps, 'adSlot'>) => (
  <AdSenseComponent
    adSlot={ADS_CONFIG.AD_SLOTS.HEADER}
    adFormat="auto"
    className={className}
    {...props}
  />
)

export const SidebarAd = ({ className, ...props }: Omit<AdSenseProps, 'adSlot'>) => (
  <AdSenseComponent
    adSlot={ADS_CONFIG.AD_SLOTS.SIDEBAR}
    adFormat="auto"
    className={className}
    {...props}
  />
)

export const FooterAd = ({ className, ...props }: Omit<AdSenseProps, 'adSlot'>) => (
  <AdSenseComponent
    adSlot={ADS_CONFIG.AD_SLOTS.FOOTER}
    adFormat="auto"
    className={className}
    {...props}
  />
) 